import { useState } from 'react';
import { useRouter } from 'next/router';
import { GoToTopNavigation } from '2-feature/Navgation/GoToTopNavigation';
import { MainRegion } from '1-project/MainRegion/MainRegion';
import { UI_JobListFilterCard } from 'ui/components/3-organisms/JobListFilterCard/JobListFilterCard';
import { UI_JobsList } from 'ui/components/3-organisms/JobsList/JobsList';
import { UI_HeaderCard } from 'ui/components/2-molecules/Card/HeaderCard/HeaderCard';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { jobDetailsSuffix } from '3-foundation/Jobs/jobDetailsSuffix';
import { unsolicitedApplicationSuffix } from '3-foundation/Jobs/unsolicitedApplicationSuffix';
import { jobAgentSlug } from '3-foundation/Jobs/jobAgentSlug';
import { Speaker } from '2-feature/Speaker/Speaker';
import { LocalNavigation } from '2-feature/Navgation/LocalNavigation';
import { useDictionaryContext } from '3-foundation/Dictionary/DictionaryContext';

interface Props extends Content.Page {
	properties: Content.JobListPageProperties;
}

export const JobListPage = ({ properties, url }: Props) => {
	const { heading, jobs, hasUnsolicitedApplication, fieldsOfWorkOptions, locationOptions, texts } = properties ?? {};
	const { locale } = useRouter();
	const dictionary = useDictionaryContext();

	const suffix = jobDetailsSuffix[locale] ?? jobDetailsSuffix['kl-gl'];
	const unsolicitedApplicationLocaleSuffix = unsolicitedApplicationSuffix[locale] ?? unsolicitedApplicationSuffix['kl-gl'];
	const jobAgentLocaleSlug = jobAgentSlug[locale] ?? jobAgentSlug['kl-gl'];
	const baseUrl = url.replace(/\/?$/, '');

	const [selectedFieldsOfWork, setSelectedFieldsOfWork] = useState<string[]>([]);
	const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

	const filteredJobs = jobs.filter((job) => {
		const matchesField = selectedFieldsOfWork.length === 0 || job.fieldsOfWork.some((f) => selectedFieldsOfWork.includes(f.id));
		const matchesLocation = selectedLocations.length === 0 || job.locations.some((l) => selectedLocations.includes(l.id));
		return matchesField && matchesLocation;
	});

	const handleFieldsOfWorkChange = (ids: string[]) => {
		setSelectedFieldsOfWork(ids);
	};

	const handleLocationsChange = (ids: string[]) => {
		setSelectedLocations(ids);
	};

	return (
		<MainRegion
			header={heading && <UI_HeaderCard heading={heading} reducedSpacing />}
			localNavigationOnTop
			localNavigation={
				<UI_Grid full rowGap>
					<LocalNavigation
						heading={dictionary.getJob('JobListNavigationHeading', locale)}
						additionalNavigationItems={[
							{ id: jobAgentLocaleSlug, name: dictionary.getJob('JobAgentHeading', locale), url: `${baseUrl}/${jobAgentLocaleSlug}` },
							...(hasUnsolicitedApplication
								? [
										{
											id: unsolicitedApplicationLocaleSuffix,
											name: dictionary.getJob('UnsolicitedApplicationLinkText', locale),
											url: `${baseUrl}/${unsolicitedApplicationLocaleSuffix}`,
										},
									]
								: []),
						]}
					></LocalNavigation>

					<UI_JobListFilterCard
						heading={texts.fieldsOfWorkFilterHeader}
						options={fieldsOfWorkOptions}
						selectedIds={selectedFieldsOfWork}
						onClick={handleFieldsOfWorkChange}
					/>
					<UI_JobListFilterCard
						heading={texts.locationFilterHeader}
						options={locationOptions}
						selectedIds={selectedLocations}
						onClick={handleLocationsChange}
					/>
				</UI_Grid>
			}
			withLocalNavigationArticleWidth="Narrow"
			speaker={<Speaker noBottomSpacing={true} />}
		>
			<UI_Container width="Standard">
				<UI_JobsList
					items={filteredJobs.map((job) => ({
						...job,
						endDate: job.endDate || dictionary.getJob('JobListNoDeadlineText', locale),
						detailsUrl: `${baseUrl}/${encodeURIComponent(job.id)}-${suffix}`,
					}))}
					texts={{
						titleColumnHeader: texts.titleColumnHeader,
						locationColumnHeader: texts.locationColumnHeader,
						deadlineColumnHeader: texts.deadlineColumnHeader,
						loadMoreButtonText: texts.loadMoreButtonText,
						noResultsText: texts.noResultsText,
						showingCount: (visible, total) =>
							texts.showingCountText.replace('{value1}', String(visible)).replace('{value2}', String(total)),
					}}
				/>
			</UI_Container>
			<GoToTopNavigation />
		</MainRegion>
	);
};
